import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetHomepageSettingsQuery } from "../redux/services/api";
import { setSettings } from "../redux/slices/homepageSlice";
import {
  selectSettings,
  selectHomepageLoading,
} from "../redux/slices/homepageSlice";
import { useTranslation } from "../hooks/useTranslation";
import AOS from "aos";
import "aos/dist/aos.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const Hero = () => {
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const loading = useSelector(selectHomepageLoading);
  const { data: settingsData } = useGetHomepageSettingsQuery();
  const { t, isArabic } = useTranslation();

  useEffect(() => {
    if (settingsData && settingsData.success) {
      dispatch(setSettings(settingsData.settings));
    }
  }, [settingsData, dispatch]);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  // All hooks must be called before any conditional returns
  const [isMobile, setIsMobile] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const swiperRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const heroStyle = {
    backgroundImage: settings?.branding?.heroBackground
      ? `url(${settings.branding.heroBackground})`
      : undefined,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };

  // Wait for settings to load
  if (loading || !settings) {
    return (
      <section className="hero" id="home">
        <div className="container">
          <div className="hero-content">
            <div className="hero-logo">
              <div className="hero-logo-3d">S</div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const heroSlider = settings?.heroSlider || [];

  // Show hero slider if available (both mobile and desktop)
  if (heroSlider.length > 0) {
    return (
      <section className="hero hero-slider-mobile" id="home" dir={isArabic ? "rtl" : "ltr"}>
        <Swiper
          ref={swiperRef}
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={0}
          slidesPerView={1}
          navigation={heroSlider.length > 1}
          pagination={{
            clickable: true,
            bulletClass: "swiper-pagination-bullet",
            bulletActiveClass: "swiper-pagination-bullet-active",
          }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          loop={heroSlider.length > 1}
          className="hero-swiper"
          dir={isArabic ? "rtl" : "ltr"}
          key={isArabic ? "rtl" : "ltr"}
          onTransitionStart={(swiper) => {
            if (!swiper) return;
            // Reset all slides when transition starts
            const slides = swiper.slides || [];
            slides.forEach((slide) => {
              const slideTitle = slide.querySelector('.hero-slide-title');
              const slideDescription = slide.querySelector('.hero-slide-description');
              const slideButton = slide.querySelector('.hero-slide-button');
              if (slideTitle) {
                slideTitle.classList.remove('slide-animated');
              }
              if (slideDescription) {
                slideDescription.classList.remove('slide-animated');
              }
              if (slideButton) {
                slideButton.classList.remove('slide-animated');
              }
            });
          }}
          onTransitionEnd={(swiper) => {
            if (!swiper) return;
            setActiveSlideIndex(swiper.realIndex || 0);
            // Trigger animations after transition ends
            const activeIndex = swiper.activeIndex;
            const slides = swiper.slides || [];
            
            setTimeout(() => {
              if (slides[activeIndex]) {
                const activeSlide = slides[activeIndex];
                const title = activeSlide.querySelector('.hero-slide-title');
                const description = activeSlide.querySelector('.hero-slide-description');
                const button = activeSlide.querySelector('.hero-slide-button');
                
                if (title) {
                  title.classList.add('slide-animated');
                }
                if (description) {
                  setTimeout(() => {
                    description.classList.add('slide-animated');
                  }, 200);
                }
                if (button) {
                  setTimeout(() => {
                    button.classList.add('slide-animated');
                  }, 400);
                }
              }
            }, 50);
          }}
          onSlideChange={(swiper) => {
            if (!swiper) return;
            setActiveSlideIndex(swiper.realIndex || 0);
          }}
          onSwiper={(swiper) => {
            if (!swiper) return;
            // Trigger initial animation
            setTimeout(() => {
              const activeIndex = swiper.activeIndex;
              const slides = swiper.slides || [];
              if (slides[activeIndex]) {
                const activeSlide = slides[activeIndex];
                const title = activeSlide.querySelector('.hero-slide-title');
                const description = activeSlide.querySelector('.hero-slide-description');
                const button = activeSlide.querySelector('.hero-slide-button');
                
                if (title) {
                  title.classList.add('slide-animated');
                }
                if (description) {
                  setTimeout(() => {
                    description.classList.add('slide-animated');
                  }, 200);
                }
                if (button) {
                  setTimeout(() => {
                    button.classList.add('slide-animated');
                  }, 400);
                }
              }
            }, 200);
          }}
        >
          {heroSlider.map((slide, index) => (
            <SwiperSlide key={index}>
              <div
                className="hero-slide"
                style={{
                  backgroundImage: slide.image
                    ? `url(${slide.image})`
                    : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div className="container">
                  <div className="hero-slide-content">
                    {slide.title && (
                      <h1 className="hero-slide-title">
                        {isArabic && slide.titleAr ? slide.titleAr : slide.title}
                      </h1>
                    )}
                    {slide.description && (
                      <p className="hero-slide-description">
                        {isArabic && slide.descriptionAr
                          ? slide.descriptionAr
                          : slide.description}
                      </p>
                    )}
                    {slide.buttonText && slide.buttonLink && (
                      <a
                        href={slide.buttonLink}
                        className="hero-slide-button"
                      >
                        {isArabic && slide.buttonTextAr
                          ? slide.buttonTextAr
                          : slide.buttonText}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>
    );
  }

  // Fallback: Show regular hero if no slider
  return (
    <section className="hero" id="home" style={heroStyle}>
      <div className="container">
        <div className="hero-content" data-aos="fade-up" data-aos-delay="200">
          {settings.branding?.logo ? (
            <div className="hero-logo">
              <img
                src={settings.branding.logo}
                alt={
                  isArabic && settings.branding?.companyNameAr
                    ? settings.branding.companyNameAr
                    : settings.branding?.companyName || "Company Logo"
                }
                className="hero-logo-image"
              />
            </div>
          ) : (
            <div className="hero-logo">
              <div className="hero-logo-3d">S</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
